import type {
  BusinessLicenseDocument,
  CrbServiceProvider,
  CreateDocumentsRequest,
  DocumentUpload,
  DocumentUploadRoot,
  DocumentUploadSymlink,
  DueDiligenceDocument,
  OnboardingDocumentRequirement,
  OnboardingDocumentRequirementResult,
  TemplateResponse,
  TemplateResultResponse
} from '@gcv/shared';
import {
  DueDiligenceRequirementStatus,
  DueDiligenceStatus,
  MarketplaceOfferCategory,
  RequirementResultType,
  RequirementType
} from '@gcv/shared';
import { inject, injectable } from 'inversify';
import { action, makeAutoObservable, observe } from 'mobx';

import { MyProvidersStatus } from 'domain/enums';
import { CommentStore } from 'stores/CommentStore';
import { ComplianceStore } from 'stores/ComplianceStore';
import { CrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { SnackbarStore } from 'stores/SnackBarStore';
import type { Row } from 'ui';
import { getCustomFieldsLastModifiedDateText } from 'util/additional-info.util';
import { DateTimeHelpers } from 'util/dateTime.util';
import { createPlaceholderRequirementData, getOnboardingRequirementsForCrb } from 'util/due-diligence.util';
import { getMimeType, openFileInNewWindow } from 'util/files.util';
import { getObjectMap } from 'util/objectUtils';
import { viewDocument } from 'util/s3.util';
import { MyProvidersRepo } from '../../my-providers.repo';
import type { PDPM } from './provider-details.repo';
import { ProviderDetailsRepo } from './provider-details.repo';

export interface DocumentTableData {
  requirement: string;
  number_of_documents: number;
  type: string;
  last_updated: string;
  lifecycle_enabled?: boolean;
}
export interface PDVM {
  loading: boolean;
  loadingAdditionalInfo: boolean;
  savingAdditionalInfo: boolean;
  loadingRequirement: boolean;
  template: TemplateResponse | null;
  templateResult: TemplateResultResponse | null;
  details: CrbServiceProvider | null;
  allDocumentsMap: { [id: string]: DocumentUpload };
  documentRows: Row<DocumentTableData>[];
  additionalInfoEditMode: boolean;
  logoUrl?: string;
  dueDiligenceStatus: MyProvidersStatus;
  providerCategory: ReadableCategory;
  ddSubmittedBy: string;
  ddApprovedDate: string;
  ddLastUpdated: string;
  additionalInfoLastUpdated: string;
  docRequirement: OnboardingDocumentRequirement | null;
  docRequirementResult: OnboardingDocumentRequirementResult;
  isBusinessLicense: boolean;
  currentDocument?: DocumentUpload;
  uploadedBy?: string;
  currentDocumentMetadata?: DueDiligenceDocument;
  license?: BusinessLicenseDocument;
  sharedRootDocs: {
    requirement: OnboardingDocumentRequirement;
    ddDocuments: DueDiligenceDocument[];
    documents: DocumentUpload[];
  }[];
  dispensaryId: string;
}

export enum ReadableCategory {
  banking = 'Banking',
  lending = 'Lending',
  insurance = 'Insurance',
  professional_services = 'Professional Services',
  payments = 'Payments',
  logistics = 'Logistics',
  hr_payroll = 'HR Payroll',
  other = 'Other'
}

@injectable()
export class ProviderDetailsPresenter {
  @inject(MyProvidersRepo)
  private parentRepo: MyProvidersRepo;

  @inject(ProviderDetailsRepo)
  private repo: ProviderDetailsRepo;

  @inject(SnackbarStore)
  private snackbarStore: SnackbarStore;

  @inject(ComplianceStore)
  private complianceStore: ComplianceStore;

  @inject(CrbDispensaryStore)
  crbDispensaryStore: CrbDispensaryStore;

  @inject(CommentStore)
  private commentStore: CommentStore;

  constructor() {
    makeAutoObservable(this);
  }

  public viewModel: PDVM = {
    loading: true,
    loadingAdditionalInfo: true,
    savingAdditionalInfo: false,
    loadingRequirement: true,
    template: null,
    templateResult: null,
    details: null,
    allDocumentsMap: {},
    documentRows: [],
    docRequirement: null,
    additionalInfoEditMode: false,
    dueDiligenceStatus: MyProvidersStatus.NotStarted,
    providerCategory: ReadableCategory.banking,
    ddSubmittedBy: '--',
    ddApprovedDate: '--',
    ddLastUpdated: '--',
    additionalInfoLastUpdated: '',
    isBusinessLicense: false,
    docRequirementResult: {
      id: '',
      do_not_have: false,
      do_not_have_comments: '',
      documents: {},
      reviewed_by: '',
      review_date: '',
      status: DueDiligenceRequirementStatus.NEEDS_REVIEW,
      type: RequirementResultType.OnboardingDocumentRequirementResult,
      crb_id: '',
      date_created: '',
      date_updated: '',
      requirement_id: '',
      template_id: '',
      template_requirement_id: '',
      requirement_version: ''
    },
    sharedRootDocs: [],
    dispensaryId: ''
  };

  private updateViewModel = action((viewModel: Partial<PDVM>) => {
    this.viewModel = { ...this.viewModel, ...viewModel };
  });

  private loadStore = action(async (providerId: string, details: CrbServiceProvider | null) => {
    try {
      await this.repo.load(providerId, details);
    } catch (e) {
      const error = e as Error;
      this.snackbarStore.showErrorSnackbarMessage(
        "There was an error loading the provider's details. Please ensure you are logged into the correct org or contact support for additional help."
      );
    }
  });

  public load = action(async (providerId: string) => {
    this.updateViewModel({
      loading: true,
      loadingAdditionalInfo: true,
      loadingRequirement: true,
      dispensaryId: this.crbDispensaryStore.currentDispensary.id
    });

    observe(this.repo, 'programmersModel', (obj) => {
      const programmersModel = obj.newValue as PDPM;
      this.updateViewModel({
        template: programmersModel.template,
        templateResult: programmersModel.templateResult,
        details: programmersModel.details,
        logoUrl:
          programmersModel.details?.templates.value.onboarding.marketplace_offer_snapshot?.image_url ??
          details?.theme?.logo_s3_key,
        dueDiligenceStatus: this.convertDueDiligenceStatus(
          programmersModel.templateResult?.status as DueDiligenceStatus
        ),
        providerCategory:
          ReadableCategory[
            (programmersModel.details?.templates.value.onboarding.marketplace_offer_snapshot
              ?.offer_category as MarketplaceOfferCategory) || (MarketplaceOfferCategory.Banking as 'banking')
          ],
        ddSubmittedBy: programmersModel.templateResult?.submitted_by
          ? `${
              this.crbDispensaryStore.currentDispensaryStaff.find(
                (u) => u.id === programmersModel.templateResult?.submitted_by
              )?.firstName
            } ${
              this.crbDispensaryStore.currentDispensaryStaff.find(
                (u) => u.id === programmersModel.templateResult?.submitted_by
              )?.lastName
            }`
          : '--',
        ddApprovedDate: DateTimeHelpers.formatISOToTableDateString(
          programmersModel.templateResult?.reviewed_date,
          this.crbDispensaryStore.currentDispensary.iana_timezone
        ),
        ddLastUpdated: DateTimeHelpers.formatISOToTableDateString(
          programmersModel.details?.templates.value.onboarding.last_updated,
          this.crbDispensaryStore.currentDispensary.iana_timezone
        ),
        additionalInfoLastUpdated: getCustomFieldsLastModifiedDateText(programmersModel.templateResult)
      });
      if (this.viewModel.template && this.viewModel.templateResult) {
        this.makeDocumentRows(this.viewModel.template, this.viewModel.templateResult);
      }
    });

    let details: CrbServiceProvider | null = null;
    if (this.parentRepo.programmersModel.providers) {
      details = this.parentRepo.programmersModel.providers[providerId];
    }
    await this.loadStore(providerId, details);

    this.updateViewModel({
      loading: false,
      loadingAdditionalInfo: false,
      loadingRequirement: false,
      sharedRootDocs: this.repo.programmersModel.sharedRootDocs
    });
  });

  public fileUpload = async (requirementId: string, fieldId: string, file: File) => {
    const doc = await this.repo.uploadOnboardingTemplateDocument(requirementId, fieldId, file);
    if (doc) {
      const updatedDocMap = {
        ...this.viewModel.allDocumentsMap
      };
      updatedDocMap[doc.id] = doc;
      this.updateViewModel({ allDocumentsMap: updatedDocMap });
    }
    return doc;
  };

  public submitForm = async () => {
    this.updateViewModel({ loading: true, additionalInfoEditMode: false });
    try {
      await this.repo.saveAndCompleteOnboardingTemplate();
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was an error saving the form data. Please contact support for additional help.'
      );
    } finally {
      this.snackbarStore.showSuccessSnackbarMessage('Additional info has been updated successfully.');
    }

    this.updateViewModel({ loading: false });
  };

  public editForm = (editFormOn: boolean) => {
    this.updateViewModel({ additionalInfoEditMode: editFormOn });
  };

  public deleteOnboardingTemplateDocument = async (
    requirementId: string,
    fieldId: string,
    file: DocumentUpload
  ) => {
    this.updateViewModel({ loadingAdditionalInfo: true });

    if (this.viewModel.template) {
      await this.repo.deleteOnboardingTemplateDocument(file);

      const updatedDocMap = {
        ...this.viewModel.allDocumentsMap
      };
      delete updatedDocMap[file.id];
      this.updateViewModel({ allDocumentsMap: updatedDocMap });
    }
    this.updateViewModel({ loadingAdditionalInfo: false });
  };

  private formatText = (text: string) => {
    if (!text) {
      return '';
    }

    // remove underscores
    text = text.replace('_', ' ');

    // capitalize letters
    text = text.replace(/(?:^|\s)\S/g, function (a) {
      return a.toUpperCase();
    });

    return text;
  };

  public makeDocumentRows = action(
    async (template: TemplateResponse, templateResult: TemplateResultResponse) => {
      const requirementsMetadataMap = getObjectMap(templateResult.requirements_results, 'requirement_id');
      const dueDiligenceTemplate = getOnboardingRequirementsForCrb(
        template,
        this.complianceStore.licenseDataMap['CA']?.employee_license_required
      );
      const rows = Object.keys(dueDiligenceTemplate).map((reqId) => {
        const lastUpdated = requirementsMetadataMap[reqId]?.date_updated;
        const type = dueDiligenceTemplate[reqId].category;

        return {
          id: reqId,
          data: {
            requirement: this.formatText(dueDiligenceTemplate[reqId].name),
            number_of_documents: requirementsMetadataMap[reqId]
              ? Object.values(requirementsMetadataMap[reqId].documents).length
              : 0,
            type: this.formatText(type),
            last_updated: lastUpdated ?? '',
            lifecycle_enabled: dueDiligenceTemplate[reqId].lifecycle_config?.lifecycle_enabled
          }
        };
      });
      this.updateViewModel({ documentRows: rows });
    }
  );

  public getDocuments = action(async () => {
    this.updateViewModel({ loadingRequirement: true });
    const documentsMap = await this.repo.getDocuments(this.crbDispensaryStore.currentDispensary.id);
    this.updateViewModel({ allDocumentsMap: documentsMap, loadingRequirement: false });
  });

  public addDocumentToOnboardingDocumentRequirement = action(
    async (requirementId: string | undefined, documents: CreateDocumentsRequest) => {
      this.updateViewModel({ loadingRequirement: true });
      if (requirementId) {
        const templateReqResponse = await this.repo.addDocToOnboarding(requirementId, documents);
        if (templateReqResponse) {
          const templateResponse: TemplateResultResponse = {
            ...(this.viewModel.templateResult as TemplateResultResponse)
          };
          const indx = templateResponse.requirements_results.findIndex((requirement) => {
            return requirement.requirement_id === requirementId;
          });
          if (indx > -1) {
            templateResponse.requirements_results[indx] = templateReqResponse;
          } else {
            templateResponse.requirements_results?.push(templateReqResponse);
          }
          this.updateViewModel({ templateResult: templateResponse });
        }
        await this.getDocuments();
      }
      this.updateViewModel({ loadingRequirement: false });
    }
  );

  public setRequirement = action(async (requirementId: string) => {
    const currentRequirement = this.viewModel.template?.requirements[requirementId];
    if (currentRequirement) {
      this.updateViewModel({
        docRequirement: currentRequirement,
        isBusinessLicense: currentRequirement.type === RequirementType.BusinessLicense
      });
    }
    this.updateViewModel({
      loadingRequirement: false,
      loading: false
    });
  });

  public setRequirementResult = action((requirementId: string) => {
    if (this.viewModel.docRequirement) {
      const requirementData =
        this.viewModel.templateResult?.requirements_results.find((r) => r.requirement_id === requirementId) ??
        createPlaceholderRequirementData(this.viewModel.docRequirement);

      requirementData.documents = Object.values(requirementData.documents).reduce((acc, curr) => {
        acc = { ...acc, [curr.id]: curr };

        return acc;
      }, {});
      this.updateViewModel({ docRequirementResult: requirementData });
    }
  });

  autoSaveAdditionalInfoSection = action(
    async (sectionId: string, customFieldValues: { responses: Record<string, any> }) => {
      this.updateViewModel({ savingAdditionalInfo: true });
      await this.repo.autoSaveAdditionalInfoSection(sectionId, customFieldValues);
      this.updateViewModel({ savingAdditionalInfo: false });
    }
  );

  public convertDueDiligenceStatus = (status: DueDiligenceStatus): MyProvidersStatus => {
    switch (status) {
      case DueDiligenceStatus.BANK_APPROVED:
        return MyProvidersStatus.Active;
      case DueDiligenceStatus.BANK_AWAITING_REVIEW:
        return MyProvidersStatus.Submitted;
      case DueDiligenceStatus.BANK_DISCONNECTED:
        return MyProvidersStatus.Inactive;
      case DueDiligenceStatus.BANK_IN_PROGRESS:
        return MyProvidersStatus.Started;
      case DueDiligenceStatus.BANK_PENDING:
        return MyProvidersStatus.Started;
      case DueDiligenceStatus.BANK_REVIEW_IN_PROGRESS:
        return MyProvidersStatus.UnderReview;
      case DueDiligenceStatus.GCV_APPROVED:
        return MyProvidersStatus.Active;
      case DueDiligenceStatus.GCV_AWAITING_REVIEW:
        return MyProvidersStatus.NotStarted;
      case DueDiligenceStatus.GCV_IN_PROGRESS:
        return MyProvidersStatus.Started;
      case DueDiligenceStatus.GCV_PENDING:
        return MyProvidersStatus.NotStarted;
      case DueDiligenceStatus.UPDATE_REQUIRED:
        return MyProvidersStatus.UnderReview;
      default:
        return MyProvidersStatus.NotStarted;
    }
  };

  public openComments = () => {
    this.commentStore.openComments(true);
  };

  private getRootDocId = (linked_doc_id: string): string => {
    const linkedDocArray = linked_doc_id.split('_');
    return linkedDocArray[1];
  };

  public viewDocumentDetails = (docId: string) => {
    let doc = this.viewModel.allDocumentsMap[docId];
    const docMetaData = this.viewModel.docRequirementResult.documents[doc.id];
    const user = this.crbDispensaryStore.currentDispensaryStaff.find((u) => u.id === doc.uploaded_by);
    if (!user && (doc as DocumentUploadSymlink).linked_doc_id) {
      const rootDoc =
        this.viewModel.allDocumentsMap[this.getRootDocId((doc as DocumentUploadSymlink).linked_doc_id)];
      if ((rootDoc as DocumentUploadRoot)?.third_party_upload_org_id) {
        doc = rootDoc;
      }
    }
    if (this.viewModel.docRequirement?.type === RequirementType.BusinessLicense) {
      if ((this.viewModel.allDocumentsMap[docId] as DocumentUploadSymlink).linked_doc_id) {
        for (const srd of this.viewModel.sharedRootDocs) {
          if (srd.requirement.type === RequirementType.BusinessLicense) {
            for (const dd of srd.ddDocuments) {
              if (
                dd.id ===
                this.getRootDocId(
                  (this.viewModel.allDocumentsMap[docId] as DocumentUploadSymlink).linked_doc_id
                )
              ) {
                this.updateViewModel({
                  license: dd as BusinessLicenseDocument
                });
              }
            }
          }
        }
      } else {
        this.updateViewModel({
          license: this.viewModel.docRequirementResult.documents[docId] as BusinessLicenseDocument
        });
      }
    }

    const uploadedBy = user ? `${user.firstName} ${user.lastName}` : this.viewModel.details?.orgName;
    this.updateViewModel({
      currentDocumentMetadata: docMetaData,
      currentDocument: doc,
      uploadedBy
    });
    return { docMetaData, doc };
  };

  public openDocument = async (docId: string, file_name: string) => {
    const blob = await viewDocument({ orgId: this.crbDispensaryStore.currentDispensary.id }, docId);

    if (blob) {
      openFileInNewWindow(blob, getMimeType(file_name), file_name);
    }
  };

  addNewDocuments = action((docs: DocumentUpload[]) => {
    for (const doc of docs) {
      this.viewModel.allDocumentsMap[doc.id] = doc;
    }
    this.updateViewModel({ allDocumentsMap: this.viewModel.allDocumentsMap });
  });
}
